// ============================================================================
// USE KANBAN STYLE PREFERENCE HOOK
// Loads and caches the client's preferred kanban board style
// Date: January 2026
// ============================================================================

import { useState, useEffect } from 'react';
import { authService } from '@/lib/authService';
import { USER_KANBAN_STYLE_PREFERENCE_KEY } from '@/lib/work-items/userBoardStylePreference';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

// Default style if none is set
const DEFAULT_KANBAN_STYLE = 'classic';

// Cache the style in memory to avoid repeated API calls
let cachedStyle: string | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to get the client's preferred kanban board style
 * Loads from API on first call, then caches for subsequent calls
 */
export function useKanbanStylePreference() {
  const [style, setStyle] = useState<string>(cachedStyle || DEFAULT_KANBAN_STYLE);
  const [loading, setLoading] = useState(!cachedStyle);

  useEffect(() => {
    // If cache is still valid, use it
    if (cachedStyle && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setStyle(cachedStyle);
      setLoading(false);
      return;
    }

    // Otherwise fetch from API
    const loadPreference = async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) {
          setLoading(false);
          return;
        }

        const preferencesResponse = await fetch('/api/v1/preferences', {
          headers: { 'Authorization': `Bearer ${tokens.access_token}` }
        });

        if (preferencesResponse.ok) {
          const data = await preferencesResponse.json();
          const preset = typeof data?.preferences?.[USER_KANBAN_STYLE_PREFERENCE_KEY] === 'string'
            ? data.preferences[USER_KANBAN_STYLE_PREFERENCE_KEY]
            : null;

          if (preset) {
            cachedStyle = preset;
            cacheTimestamp = Date.now();
            setStyle(preset);
            return;
          }
        }

        const response = await fetch('/api/v1/industry-solutions/config', {
          headers: { 'Authorization': `Bearer ${tokens.access_token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.config?.kanban_style_preset) {
            const preset = data.config.kanban_style_preset;
            cachedStyle = preset;
            cacheTimestamp = Date.now();
            setStyle(preset);
          }
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('Failed to load kanban style preference:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreference();
  }, []);

  return { style, loading };
}

/**
 * Update the cached style (call this after saving a new preference)
 */
export function updateKanbanStyleCache(newStyle: string) {
  cachedStyle = newStyle;
  cacheTimestamp = Date.now();
}

/**
 * Clear the cache (call this on logout or client switch)
 */
export function clearKanbanStyleCache() {
  cachedStyle = null;
  cacheTimestamp = 0;
}
